/**
 * Wires overlap detection between pooled projectiles and hurtboxes, and
 * between dedicated melee hitboxes and hurtboxes. Hurtboxes are plain
 * Arcade bodies sized independently from the owning sprite's visual frame
 * (set via entity.hurtbox), so damage volumes stay tunable without
 * touching art.
 */
export class HitboxManager {
  constructor(scene) {
    this.scene = scene;
    this._colliders = [];
  }

  /**
   * Player/enemy projectile pool vs. a group of hurtboxes (enemies or player).
   *
   * Phaser's Arcade overlap callback does NOT reliably preserve the
   * (object1, object2) order you pass to `physics.add.overlap()` once one
   * side is a Group and the other a single GameObject — it can flip them
   * depending on which internal collide routine it dispatches to. So we
   * identify the projectile by pool membership rather than by position.
   */
  watchProjectiles(pool, targetGroupOrObj, onHit) {
    const collider = this.scene.physics.add.overlap(
      pool.group,
      targetGroupOrObj,
      (a, b) => {
        const proj = pool.group.contains(a) ? a : b;
        const target = proj === a ? b : a;
        if (!proj.active) return;
        if (!target || target.getData('team') === proj.getData('team')) return;
        if (target.getData?.('invulnerable')) {
          pool.recycle(proj);
          return;
        }
        onHit(target, proj.getData('damage'), proj);
        pool.recycle(proj);
      },
      undefined,
      this
    );
    this._colliders.push(collider);
    return collider;
  }

  /** A single melee/attack hitbox zone vs. a hurtbox group — used for aggro-state attacks. */
  watchMeleeHitbox(hitboxZone, targetGroupOrObj, onHit) {
    const collider = this.scene.physics.add.overlap(
      hitboxZone,
      targetGroupOrObj,
      (a, b) => {
        const zone = a === hitboxZone ? a : b;
        const target = zone === a ? b : a;
        if (!zone.getData('active')) return;
        if (!target || target.getData?.('invulnerable')) return;
        onHit(target, zone.getData('damage') ?? 1);
      },
      undefined,
      this
    );
    this._colliders.push(collider);
    return collider;
  }

  destroy() {
    for (const c of this._colliders) c.destroy();
    this._colliders = [];
  }
}
